import { useEffect, useRef, useState } from "react"
import {ThunkDispatch} from "@reduxjs/toolkit";
import {connect, useDispatch, useSelector } from "react-redux"
import {useNavigate} from 'react-router-dom'
import {editPhotoActions} from '../../reducers/editPhoto'
import { Modal } from "flowbite-react";
import {CircleOutlineIcon, RedoIcon, SquareOutlineIcon, TriangleOutlineIcon, UndoIcon} from '../SVGIcons'
import {selectAfterImageData, selectServiceSettings, selectBeforeImageData} from '../../selectors'
import {roomDesignActions} from '../../reducers/roomDesign'
import { Slider } from 'primereact/slider';
import Drawing from './Drawing'
import { isWatermarkEnabled } from '../../utils/watermark'
import AIEraserConfirmPopup from './AIEraserConfirmPopup'
import { getRandId } from '../../libs'
import { RoomType } from '../../constants'

type EraserShape = "circle" | "triangle" | "square"

// テストモード設定: 非同期処理のシミュレーション用
// trueにすると、ポップアップを開いてから指定秒数後にsetAfterImageRefがdispatchされる
const TEST_MODE = false
const TEST_DELAY_MS = 5000

function mapStateToProps(state: any) {
  const {selectedAfterImageData} = selectAfterImageData(state);
  return { ...state.roomDesign,
    selectedAfterImageData
   }
}

const RoomDesignEditPhotoPopup = (props: any) => {
  const {curHead, curHeadSize, lines, historyIndex, historyStack, showWatermarkInEditor, isAIEraserMode, aiEraserUsageByImage, currentImageId, aiEraserMaxUsage, aiEraserLines}  = useSelector((state: any) => state.editPhoto)
  const {beforeImageRef, afterImageRef, watermarkGlobalEnabled} = useSelector((state: any) => state.roomDesign)
  const selectedBeforeImageData = useSelector((state: any) => selectBeforeImageData(state))
  const watermarkSettings = useSelector((state: any) => selectServiceSettings(state))
  const {
    isEditPhotoPopupShow,
  } = props
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
  const navigate = useNavigate()
  
  // AI消しゴムの残り使用回数を計算
  const currentUsage = currentImageId ? (aiEraserUsageByImage[currentImageId] || 0) : 0
  const aiEraserRemainingUsage = aiEraserMaxUsage - currentUsage

  // 前回のポップアップ表示状態を追跡
  const prevIsEditPhotoPopupShowRef = useRef(false)

  // ポップアップが開いた時の初期化処理
  useEffect(() => {
    // falseからtrueになった時のみ初期化（ポップアップが開いた時）
    if (isEditPhotoPopupShow && !prevIsEditPhotoPopupShowRef.current) {

      // ポップアップを開く時は、まずstageをクリアしてから初期化
      dispatch(editPhotoActions.clearStageReferences())

      // 次のイベントループで初期化（stageクリアが反映された後）
      setTimeout(() => {
        dispatch(editPhotoActions.setInitState())

        // 編集用に現在の画像参照をキャプチャ（外部からの変更を無視するため）
        if (beforeImageRef && afterImageRef) {
          dispatch(editPhotoActions.setCapturedImageRefs({
            beforeImageRef,
            afterImageRef
          }))
        }

        // ウォーターマーク表示を結果画面の設定と同期（サービス設定が有効な場合）
        const hasWatermarkSettings = watermarkSettings && isWatermarkEnabled(watermarkSettings)
        dispatch(editPhotoActions.setShowWatermarkInEditor(hasWatermarkSettings && watermarkGlobalEnabled))

        // 画像IDを設定（各beforeImageのstubIdを使用）
        if (beforeImageRef && selectedBeforeImageData?.stubId) {
          // 各画像の個別IDを使用してAI消しゴムの使用回数を管理
          const imageId = `room_${selectedBeforeImageData.stubId}`
          dispatch(editPhotoActions.setCurrentImageId(imageId))
        }

        // 初期画像を保存
        if (afterImageRef) {
          const initialImage = afterImageRef.src || afterImageRef
          dispatch(editPhotoActions.setInitialImageRef(initialImage))
        }

        // ワンルームの場合は自動的にAI消しゴムモードにする
        if (selectedBeforeImageData?.roomType === RoomType['ワンルーム']) {
          dispatch(editPhotoActions.setAIEraserMode(true))
        }
      }, 0)
    }

    // 前回の状態を更新
    prevIsEditPhotoPopupShowRef.current = isEditPhotoPopupShow

  }, [isEditPhotoPopupShow, watermarkGlobalEnabled, watermarkSettings, beforeImageRef, afterImageRef, selectedBeforeImageData?.stubId])

  // テストモード: 非同期完了のシミュレーション
  // 編集画面を開いてから指定秒数後に、外部からafterImageRefが更新されるケースを再現
  useEffect(() => {
    if (!TEST_MODE || !isEditPhotoPopupShow) return

    const timer = setTimeout(() => {
      // 現在のafterImageRefをそのまま再設定（実際の非同期完了と同じ挙動）
      if (afterImageRef) {
        dispatch(roomDesignActions.setAfterImageRef(afterImageRef))
      }
    }, TEST_DELAY_MS)

    return () => clearTimeout(timer)
  }, [isEditPhotoPopupShow, afterImageRef, dispatch])

  const closePopup = () => {
    dispatch(roomDesignActions.setIsEditPhotoPopupShow(false))
    // stageのクリアは次回開く時に行うので、ここでは不要
  }
  const handleSave = () => {
    dispatch(roomDesignActions.setIsSaveChangesPhotoPopupShow(true))
  }
  const handleDiscard = () => {
    // 破棄ダイアログを表示（実際のクローズはダイアログ側で処理）
    dispatch(roomDesignActions.setIsDiscardChangesPhotoPopupShow(true))
  }
  const handleUndo = () => {
    dispatch(editPhotoActions.undo())
    // 履歴から画像を復元
    const targetIndex = Math.max(0, historyIndex - 1)
    const currentHistory = historyStack[targetIndex]

    if (currentHistory && currentHistory.imageRef) {
      // imageRefが文字列（DataURL）の場合、HTMLImageElementに変換
      if (typeof currentHistory.imageRef === 'string') {
        const img = new Image()
        img.onload = () => {
          dispatch(roomDesignActions.setAfterImageRef(img))
          dispatch(editPhotoActions.setCapturedAfterImageRef(img))
          // 画像読み込み後に再描画
          setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), 10)
        }
        img.src = currentHistory.imageRef
      } else {
        dispatch(roomDesignActions.setAfterImageRef(currentHistory.imageRef))
        dispatch(editPhotoActions.setCapturedAfterImageRef(currentHistory.imageRef))
        // 再描画を明示的に実行してAI消しゴムの線も含めて表示
        setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), 10)
      }
    } else {
      // imageRefがない場合（初期状態）は何もしない（元の画像がそのまま使われる）
      // 再描画を明示的に実行
      setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), 10)
    }
  }

  const handleRedo = () => {
    dispatch(editPhotoActions.redo())
    // 履歴から画像を復元
    const currentHistory = historyStack[Math.min(historyStack.length - 1, historyIndex + 1)]
    if (currentHistory && currentHistory.imageRef) {
      // imageRefが文字列（DataURL）の場合、HTMLImageElementに変換
      if (typeof currentHistory.imageRef === 'string') {
        const img = new Image()
        img.onload = () => {
          dispatch(roomDesignActions.setAfterImageRef(img))
          dispatch(editPhotoActions.setCapturedAfterImageRef(img))
          // 画像読み込み後に再描画
          setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), 10)
        }
        img.src = currentHistory.imageRef
      } else {
        dispatch(roomDesignActions.setAfterImageRef(currentHistory.imageRef))
        dispatch(editPhotoActions.setCapturedAfterImageRef(currentHistory.imageRef))
        // 再描画を明示的に実行してAI消しゴムの線も含めて表示
        setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), 10)
      }
    } else {
      // 再描画を明示的に実行
      setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), 10)
    }
  }

  const handleClearAll = () => {
    dispatch(editPhotoActions.clearAll())
    // 明示的に再描画を実行して画面をクリア
    setTimeout(() => dispatch(editPhotoActions.drawPreviewImage({})), 10)
  }

  const canUndo = historyIndex > 0
  const canRedo = historyIndex < historyStack.length - 1

  // ウォーターマークが設定されているかチェック（マイページ設定のみで判定）
  const hasWatermark = watermarkSettings && isWatermarkEnabled(watermarkSettings)

  // 履歴がある場合はモード切り替えを無効化
  const hasDrawingHistory = lines.length > 0 || aiEraserLines.length > 0

  // ワンルームの判定
  const isOneRoom = selectedBeforeImageData?.roomType === RoomType['ワンルーム']

  const handleWatermarkToggle = () => {
    dispatch(editPhotoActions.toggleWatermarkInEditor())
  }

  const setDefaultCurHead = (shape:EraserShape) => {
    dispatch(editPhotoActions.setDefaultCurHead(shape))
  }

  const renderEraserShape = (size:number) => {
    switch(curHead) {
      case 'circle':
        return <CircleOutlineIcon style={{stroke: "#595959", strokeWidth: "2px", width: `${size}px`, height: `${size}px`}}/>;
      case 'triangle':
        return <TriangleOutlineIcon style={{stroke: "#595959", strokeWidth: '2px', width: `${size}px`, height: `${size}px`}} />;
      case 'square':
        return <SquareOutlineIcon style={{stroke: "#595959", strokeWidth: '2px', width: `${size}px`, height: `${size}px`}} />;
      default:
        return;
    }
  }

  return (
    <>
      <Modal show={isEditPhotoPopupShow} onClose={closePopup} size="xl">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex items-end max-h-full justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl">
              <div className="py-2 px-4 bg-orange-500 text-white flex items-start justify-between">
                <div>
                  <p>• 本画面ではAIが生成した家具を部分的に消すことが可能です。</p>
                  <p>• 削除したい箇所をクリックしながら、マウスを移動させてください。</p>
                  {/* テスト用: 自動で非同期シミュレーションが実行される */}
                  {TEST_MODE && (
                    <p className="mt-1 text-yellow-200 text-xs">🧪 テストモード: {TEST_DELAY_MS / 1000}秒後に非同期完了をシミュレートします</p>
                  )}
                </div>
                <button
                  onClick={handleDiscard}
                  className="px-6 py-2 ml-4 text-white hover:bg-orange-600 rounded transition-colors font-bold flex-shrink-0"
                  style={{ fontSize: '32px', lineHeight: '1' }}
                  title="閉じる"
                >
                  ×
                </button>
              </div>
              <div className="p-4">
                <div className="flex flex-col gap-2 mb-2">
                  {/* 1行目：モード選択とメイン操作 */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      {/* AI消しゴムモード切り替えボタン */}
                      <div className="flex items-center">
                        <button
                          onClick={() => dispatch(editPhotoActions.setAIEraserMode(false))}
                          disabled={(hasDrawingHistory && isAIEraserMode) || isOneRoom}
                          className={`px-3 py-1 font-medium text-sm rounded-l ${
                            !isAIEraserMode ? 'bg-orange-500 text-white' : (hasDrawingHistory || isOneRoom) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-700'
                          }`}
                          title={isOneRoom ? 'ワンルームでは使用できません' : hasDrawingHistory && isAIEraserMode ? '履歴をクリアしてから切り替えてください' : '通常の消しゴム'}
                        >
                          消しゴム
                        </button>
                        <button
                          onClick={() => dispatch(editPhotoActions.setAIEraserMode(true))}
                          disabled={hasDrawingHistory && !isAIEraserMode}
                          className={`px-3 py-1 font-medium text-sm rounded-r ${
                            isAIEraserMode ? 'bg-orange-500 text-white' : hasDrawingHistory ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-700'
                          }`}
                          title={hasDrawingHistory && !isAIEraserMode ? '履歴をクリアしてから切り替えてください' : 'AI消しゴム'}
                        >
                          AI消しゴム
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center">
                      {/* AI消しゴムの実行ボタン（AI消しゴムの線がある場合のみ表示） */}
                      {aiEraserLines.length > 0 && (
                        <>
                          <button
                            onClick={() => dispatch(editPhotoActions.setShowAIEraserConfirm(true))}
                            className={`px-4 py-1 mr-3 font-medium rounded transition-all ${
                              aiEraserRemainingUsage > 0
                                ? 'bg-orange-500 text-white hover:bg-orange-600'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                            }`}
                            disabled={aiEraserRemainingUsage <= 0}
                            title={aiEraserRemainingUsage <= 0 ? '使用回数の上限に達しました' : 'AI消しゴムを実行'}
                          >
                            {aiEraserRemainingUsage > 0 ? (
                              <>
                                AI消しゴム実行
                                <span className="ml-1">（残り{aiEraserRemainingUsage}回）</span>
                              </>
                            ) : (
                              <>
                                <span className="line-through">AI消しゴム実行</span>
                                <span className="ml-1 text-red-500">（使用上限）</span>
                              </>
                            )}
                          </button>
                          <div className="w-[1px] h-[40px] bg-[#b9b7b8] mr-3"></div>
                        </>
                      )}
                      <button onClick={handleSave} className="px-4 py-1 mr-3 font-medium">
                        保存
                      </button>
                    </div>
                  </div>

                  {/* 2行目：描画ツール設定 */}
                  <div className="flex items-center gap-2">
                    {/* 描画スタイル選択 */}
                    <div className="flex items-center py-1 px-2 border-r border-[#b9b7b8]">
                      <div
                        onClick={() => setDefaultCurHead("circle")}
                        className="p-1 border-r border-[#f1f1f1] hover:cursor-pointer"
                      >
                        <CircleOutlineIcon
                          style={{
                            stroke: curHead === "circle" ? "#595959" : "#d7d7d7",
                            strokeWidth: "3px",
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      </div>
                      <div
                        onClick={() => setDefaultCurHead("triangle")}
                        className="p-1 border-r border-[#f1f1f1] hover:cursor-pointer"
                      >
                        <TriangleOutlineIcon
                          style={{
                            stroke: curHead === "triangle" ? "#595959" : "#d7d7d7",
                            strokeWidth: "3px",
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      </div>
                      <div
                        onClick={() => setDefaultCurHead("square")}
                        className="p-1 hover:cursor-pointer"
                      >
                        <SquareOutlineIcon
                          style={{
                            stroke: curHead === "square" ? "#595959" : "#d7d7d7",
                            strokeWidth: "3px",
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      </div>
                    </div>

                    {/* サイズスライダー */}
                    <div className="flex items-center py-1 px-2 border-r border-[#b9b7b8] gap-2">
                      <div>
                        {renderEraserShape(16)}
                      </div>
                      <div>
                        <Slider
                          value={curHeadSize}
                          onChange={(e) => {
                            dispatch(editPhotoActions.setCurHeadSize(e.value))
                          }}
                        />
                      </div>
                      <div>
                        {renderEraserShape(32)}
                      </div>
                    </div>

                    {/* Undo/Redo/クリア */}
                    <div className="flex items-center py-1 px-2 gap-2">
                      <button
                        onClick={handleUndo}
                        disabled={!canUndo}
                        className="p-1 hover:cursor-pointer disabled:opacity-50"
                        title="1つ前に戻る"
                      >
                        <UndoIcon style={{
                          fill: canUndo ? '#595959' : '#d1d1d1',
                          width: '24px',
                          height: '24px'
                        }} />
                      </button>
                      <button
                        onClick={handleRedo}
                        disabled={!canRedo}
                        className="p-1 hover:cursor-pointer disabled:opacity-50"
                        title="1つ先に進める"
                      >
                        <RedoIcon style={{
                          fill: canRedo ? '#595959' : '#d1d1d1',
                          width: '24px',
                          height: '24px'
                        }} />
                      </button>
                      <div className="w-[1px] h-[24px] bg-[#d7d7d7] mx-2"></div>
                      <button
                        onClick={handleClearAll}
                        disabled={lines.length === 0 && aiEraserLines.length === 0}
                        className="px-3 py-1 font-medium disabled:text-[#d1d1d1] text-sm"
                        title="すべてクリア"
                      >
                        クリア
                      </button>
                    </div>

                    {/* ウォーターマークチェックボックス */}
                    {hasWatermark && (
                      <div className="flex items-center py-1 px-2 border-l border-[#b9b7b8]">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showWatermarkInEditor}
                            onChange={handleWatermarkToggle}
                            className="mr-2"
                          />
                          <span className="text-sm font-medium">ウォーターマーク</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                <Drawing />
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <AIEraserConfirmPopup />
    </>
  );
};

export default connect(mapStateToProps)(RoomDesignEditPhotoPopup)
